import { format } from "date-fns";
import React, { useCallback, useContext, useEffect, useState } from "react"
import { parseJSONDateWithoutTime } from "../../utils/dateHelpers";
import "./SearchResultsTable.css";
import Client from "../../models/serviceModels/Client";
import { Link, useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from 'uuid';
import { ClientResultsContext } from "./ParticipantSearch";
import Checkbox from "../UI/Checkbox";
import { getClientById } from "../../services/clientsService";
import Loader from "../UI/Loader";
import ApiErrorMessages from "../UI/ApiErrorMessages";
import Button from "../UI/Button";
import IconButton from "../UI/IconButton";
import DataTable from "../UI/DataTable";
import useSortableData from "../../hooks/useSortableData";
import { DataTableColumn } from "../../models/DataTable";
import { sortableMergeSearchEnabled } from "../../utils/features";
import { OrganizationContext } from "../../App";
import { isDemographicVisible } from "../../utils/demographicsSchema";

const MergeSearchResultsTable = () => {
    const { mergeId = '' } = useParams();
    const ctx = useContext(ClientResultsContext);
    if (!ctx) {
        throw new Error("ClientResultsContext is missing provider");
    }
    const { clients, sortBy: serverSortBy, sortDirection: serverSortDirection, onSort: onServerSort } = ctx;
    const organization = useContext(OrganizationContext);
    const showPhone = isDemographicVisible(organization.value, 'phone');
    const columns: DataTableColumn[] = [
        { key: 'selection', label: '', sortable: false },
        { key: 'name', label: 'name', sortable: true },
        { key: 'dateOfBirth', label: 'birthdate', sortable: true },
        ...(showPhone ? [{ key: 'phoneNumber', label: 'phone number', sortable: true }] : []),
        { key: 'updated', label: 'updated', sortable: true }
    ];
    const [selectedClients, setSelectedClients] = useState<Client[]>([]);
    const [initialClientId, setInitialClientId] = useState<string>();
    const [loading, setLoading] = useState(true);
    const [apiError, setApiError] = useState<any>();
    const navigate = useNavigate();
    const getSortValue = useCallback((client: Client, key: string) => {
        switch (key) {
            case 'name': return `${client.lastName} ${client.firstName}`;
            case 'updated': return client.updated || client.created;
            default: return client[key as keyof Client];
        }
    }, []);
    const { data: locallySortedClients, sortBy: localSortBy, sortDirection: localSortDirection, onSort: onLocalSort } = useSortableData(clients || [], getSortValue);
    const useServerSorting = sortableMergeSearchEnabled && !!onServerSort;
    const displayedClients = useServerSorting ? clients || [] : locallySortedClients;
    const sortBy = useServerSorting ? serverSortBy || '' : localSortBy;
    const sortDirection = useServerSorting ? serverSortDirection || 'asc' : localSortDirection;
    const onSort = useServerSorting ? onServerSort! : onLocalSort;

    useEffect(() => {
        if (mergeId) {
            const stored = localStorage.getItem(`mergeParticipants_${mergeId}`);
            if (stored) {
                const mergeSelection = JSON.parse(stored) as { selectedClientId?: string, additionalClientIds: string[] };
                if (mergeSelection?.selectedClientId || (mergeSelection?.additionalClientIds && mergeSelection.additionalClientIds.length > 0)) {
                    setInitialClientId(mergeSelection.selectedClientId);
                    const idsToLoad = [];
                    if (mergeSelection.selectedClientId) {
                        idsToLoad.push(mergeSelection.selectedClientId);
                    } 

                    if (mergeSelection.additionalClientIds && mergeSelection.additionalClientIds.length !== 0) {
                        for (const id of mergeSelection.additionalClientIds) {
                            idsToLoad.push(id);
                        }
                    }
                    Promise.all(idsToLoad.map(i => getClientById({
                        id: i
                    }))).then(results => {
                        setSelectedClients(results);
                    }).catch(e => {
                        console.error(e);
                        setApiError(e);
                    }).finally(() => {
                        setLoading(false);
                    });
                } else {
                    setLoading(false);
                }
            } else {
                setLoading(false);
            }
        } else {
            const localMergeId = uuidv4();
            localStorage.setItem(`mergeParticipants_${localMergeId}`, JSON.stringify({
                selectedClientId: undefined,
                additionalClientIds: [],
                expiresOn: new Date().getTime() + (1000 * 60 * 60 * 24 * 5)
            }));
            navigate(`/participants/merge/${localMergeId}/search`, {
                replace: true
            });
        }
    }, []);

    const handleToggleSelectedClients = (client: Client, checked: boolean) => {
        if (checked) {
            setSelectedClients([
                ...selectedClients,
                client
            ]);
        } else {
            setSelectedClients([
                ...selectedClients.filter(sc => sc.id !== client.id)
            ]);
        }
    };

    const handleOnCancelMerge = () => {
        localStorage.removeItem(`mergeParticipants_${mergeId}`);
        navigate('/');
    };

    const handleNext = () => {
        localStorage.setItem(`mergeParticipants_${mergeId}`, JSON.stringify({
            selectedClientId: initialClientId,
            additionalClientIds: selectedClients.map(sc => sc.id),
            expiresOn: new Date().getTime() + (1000 * 60 * 60 * 24 * 5) // 5 days? 
        }));
        navigate(`/participants/merge/${mergeId}/compare`);
    };

    const handleOnClearAdditional = (clientId: string) => {
        setSelectedClients([
            ...selectedClients.filter(sc => sc.id !== clientId)
        ]);
    };

    if (loading) {
        return <Loader />;
    }

    const filteredSelectedClients = selectedClients.filter(sc => sc.id !== initialClientId);
    return (
        <div className='search-results-container'>
            <div className="row align-items-center">
                <div className="column-auto">
                    <h3>{filteredSelectedClients.length} Participant(s) to Merge</h3>
                </div>
            </div>
            {filteredSelectedClients.length !== 0 && <div className="row">
                {filteredSelectedClients.map(sc => {
                    return <div className="column-large-3 border padding-5" key={sc.id}>
                        <div className="row justify-content-between">
                            <div className="column-auto">
                                <div><Link to={`/participants/${sc.id}/profile-demographics`} target="_blank">{sc.lastName}, {sc.firstName}</Link></div>
                                <div>{sc.dateOfBirth && format(parseJSONDateWithoutTime(sc.dateOfBirth), 'MM/dd/yyyy')}</div>
                            </div>
                            <div className="column-auto">
                                <IconButton id={`clear-selection-${sc.id}`}
                                    icon={<i className="ri-close-line"></i>}
                                    className="flat small"
                                    onClick={() => handleOnClearAdditional(sc.id)} />
                            </div>
                        </div>
                    </div>
                })}
            </div>}
            <hr />
            <div className="row margin-vertical-3 justify-content-between align-items-center">
                <div className="column-auto">
                    <Button id="cancel-merge"
                        onClick={handleOnCancelMerge}
                        text="Cancel Merge"
                        className="action" />
                </div>
                <div className="column-auto">
                    <Button id="next-button"
                        onClick={handleNext}
                        text="Next"
                        disabled={selectedClients.length <= 1} />
                </div>
            </div>
            <ApiErrorMessages error={apiError} />
            {clients &&
                <div className="row justify-content-center">
                    <div className="column-medium-10">
                        <div className="row">
                            <div className="column">
                                <h2 className="text-center">Search Results</h2>
                                <DataTable
                                    columns={columns}
                                    data={displayedClients}
                                    sortBy={sortBy}
                                    sortDirection={sortDirection}
                                    onSort={onSort}
                                    sortingEnabled={sortableMergeSearchEnabled}
                                    emptyMessage="No matches for your search"
                                    renderRow={c => (
                                                <tr key={`client-result-${c.id}`} className="table-row">
                                                    <td>
                                                        <div className="row margin-vertical-2">
                                                            <div className="column-auto">
                                                                <div>
                                                                    <Checkbox id={`client-selection-${c.id}`}
                                                                        onChange={(checked) => handleToggleSelectedClients(c, checked)}
                                                                        checked={selectedClients.some(sc => sc.id === c.id)}
                                                                        disabled={initialClientId === c.id}
                                                                        containerClassName="unboxed margin-0" />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <Link to={`/participants/${c.id}/profile-demographics`} target="_blank">{c.lastName}, {c.firstName}</Link>
                                                        {initialClientId === c.id && <div className="text-sub">(already selected)</div>}
                                                    </td>
                                                    <td>{c.dateOfBirth && format(parseJSONDateWithoutTime(c.dateOfBirth), 'MM/dd/yyyy')}</td>
                                                    {showPhone && <td>{c.phoneNumber}</td>}
                                                    <td>{(c.updated && format(new Date(c.updated), 'Pp')) || (c.created && format(new Date(c.created), 'Pp'))} </td>
                                                </tr>
                                    )} />
                            </div>
                        </div>
                    </div>
                </div>
            }
            {
                clients && clients.length === 0 && <div className="row justify-content-center">
                    <div className="column-auto">
                        <p>No Matches For Your Search</p>
                    </div>
                </div>
            }
        </div >
    )
}
export default MergeSearchResultsTable;
